import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { OrderService } from '@app/core/business/order/order.service';
import { ContextService } from '@app/core/service/context.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';
import { Order } from '@app/core/business/order/order';
import { TranslateService } from '@ngx-translate/core';
import { ScanPipe } from 'ngx-pipes';
import { ProductAvailability } from '@app/core/business/product/product-availability';
import { OfferStateService } from '@app/ui/offer/offer-state.service';
import { Product } from '@app/core/business/product/product';
import { distinctUntilChanged, filter, finalize, map, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { FrancoStateService } from '@app/core/service/franco-state.service';

@UntilDestroy()
@Component({
  selector: 'app-add-to-cart',
  templateUrl: './add-to-cart.component.html',
  styleUrls: ['./add-to-cart.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddToCartComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  public theme = 'light';
  @Input()
  public productCode: string;
  @Input()
  public outer: number;
  @Input()
  public unpackedQuantity: number;
  /**
   * CLA le 10/10/2019 - A/B - passage d'un texte pour décrire une indisponibilité produit
   */
  @Input()
  public availability: ProductAvailability;
  @Input()
  public updating: boolean;
  @Input()
  public isDateCourte: boolean;

  public productQuantity: number = 0;
  public canAddProduct = false;
  public canRemoveProduct = false;
  public requestInProgress = false;
  public userActionPending = false;

  private oldQuantity = 0;
  //
  /* public showDecolise = this.contextService.fullContext.company.code == 'LAB';
  public isDecolise = this.contextService.fullContext.company.code == 'LAB'; */
  public showDecolise = false;
  public isDecolise = false;


  constructor(private contextService: ContextService, private toastrService: ToastrService, private orderService: OrderService, private cdr: ChangeDetectorRef,
    private translate: TranslateService, private francoState: FrancoStateService,private scanPipe: ScanPipe, public offerState: OfferStateService) { }

  ngOnInit(): void {
    this.contextService.fullContext$
      .pipe(untilDestroyed(this),
        // On ne s'intéresse qu'aux changements de quantité du produit courant, on map donc le contexte complet vers la quantité du produit
        map(ctx => ctx.order?.lines.find(
          l => l.productCode === this.productCode && (!!l.isDateCourte) === (!!this.isDateCourte)
        )?.quantity ?? undefined)
        ,
        // On ne fait quelque chose que si la quantité a vraiment changé
        // ⚠️ on n’applique pas les changements si une requête HTTP est en cours pour ce produit
        filter((x) => (!this.requestInProgress && this.oldQuantity !== x) || x == undefined ))
      .subscribe(newQuantity => {
        if (newQuantity != undefined) {
          this.productQuantity = newQuantity;
          this.oldQuantity = newQuantity;
        }
        else if (this.oldQuantity !== 0) {
          // La ligne a disparu de la commande (quantité retirée) → reset à 0 (pour les lignes en doublon grâce aux groupements)
          this.productQuantity = 0;
          this.oldQuantity = 0;
        }
        this.checkAddRemoveProduct();
        this.cdr.markForCheck();
      });

    this.oldQuantity = this.productQuantity;

    if (parseInt(this.unpackedQuantity?.toString(), 10) <= 0) {
      this.showDecolise = false;
      this.isDecolise = false;
    }


  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (
      simpleChanges &&  
      ((simpleChanges.availability.currentValue?.productCode &&
        !simpleChanges.availability.firstChange &&
        simpleChanges.availability.currentValue?.productCode !== simpleChanges.availability.previousValue?.productCode) ||
        (simpleChanges.isDateCourte &&
          !simpleChanges.isDateCourte.firstChange &&
          simpleChanges.isDateCourte.currentValue !== simpleChanges.isDateCourte.previousValue))
    ) {
      this.updateQuantity();
      this.checkAddRemoveProduct();
      this.francoState.refreshFranco(this.contextService.fullContext.order, this.contextService.fullContext.customer.code, this.contextService.fullContext.company.code);
    }
  }

  ngOnDestroy(): void { }

  private lineMatcher = (l: any) =>
    l.productCode === this.productCode && (!!l.isDateCourte) === (!!this.isDateCourte);

  private getMatchingLine(): any | undefined {
    return this.contextService.fullContext.order?.lines.find(this.lineMatcher);
  }

  private updateQuantity(): void {
    if (!this.requestInProgress && !this.userActionPending) {
      const line = this.contextService.fullContext.order?.lines
        .find(l => l.productCode === this.productCode && (!!l.isDateCourte) === (!!this.isDateCourte));
      this.productQuantity = line ? line.quantity : 0;
    }
  }

  private checkAddRemoveProduct(): void {
    // If there is an order
    this.canAddProduct = this.contextService.fullContext.order != null;
    this.canRemoveProduct = this.contextService.context.orderCode != null;

    // If adding product with it's status is acceptable by company
    if (
      this.canAddProduct &&
      this.contextService.fullContext.company?.settings?.canAddProductStatuses
    ) {
      this.canAddProduct = this.contextService.fullContext.company?.settings?.canAddProductStatuses.includes(
        this.contextService.fullContext.order?.status
      );
    }

    // If product is in order
    this.canRemoveProduct =
      this.canRemoveProduct && this.contextService.fullContext.order
        ? this.contextService.fullContext.order.lines.some(this.lineMatcher)
        : false;

    // If removing product with it's status is acceptable by company
    if (
      this.canRemoveProduct &&
      this.contextService.fullContext.company?.settings?.canRemoveProductStatuses
    ) {
      this.canRemoveProduct = this.contextService.fullContext.company?.settings?.canRemoveProductStatuses.includes(
        this.contextService.fullContext.order.status
      );
    }

    if (this.contextService.fullContext.order) {
      const isOrderValidated = this.contextService.fullContext.order.status == 'VALIDATED'; // à adapter selon le vrai nom du status si différent

      if (isOrderValidated) {
        if (this.isDateCourte === true) {
          this.canRemoveProduct = false;
        }

        const productAlreadyInCart = this.contextService.fullContext.order.lines.some(this.lineMatcher); // réutilise ton matcher existant
        if (productAlreadyInCart && this.isDateCourte === true) {
          this.canAddProduct = false;
        }
      }
    }
    

    if (this.availability?.availability < 2) {
      this.canAddProduct = false;
    }
  }

  /*
   * Prevent input of characters other than numeric
   */
  public onQuantityInput(event: any): void {
    const pattern = /^[0-9]+$/;

    if (event.key.length === 1 && !pattern.test(event.key)) {
      // invalid character, prevent input
      event.preventDefault();
    }
  }

  /**
   * Permet de choisir la quantité de décolisage comme quantité à commander
   * pour un article qui peut être commander en décolisage.
   */
  public onUnpackedQuantityInput(minus: boolean): void {
    const unpacked = Number(this.unpackedQuantity);
    const product = Number(this.productQuantity);
    //this.productQuantity = parseInt(this.unpackedQuantity.toString(), 10);
    if (!minus)
      this.productQuantity = product + unpacked;
    else
      this.productQuantity = product - unpacked;
  }

  /**
   * Permet de passer en mode colis complet ou décolisé
   */
  public onModeChangeClick(): void {
    this.isDecolise = !this.isDecolise;
  }

  /**
   * Possibilité de choisir la quantité de décolisage
   */
  public canSelectUnpackedQuantity(): boolean {
    // Si baisser quantité possible et quantité supérieure ou égale à quantité décolisage
    return ((this.canRemoveProduct && (this.productQuantity >= this.unpackedQuantity))
      // Si ajout quantité possible et quantité inférieure ou égale à quantité décolisage
      || (this.canAddProduct && (this.productQuantity <= this.unpackedQuantity)))
      // Si l'article est disponible
      && !(this.availability?.availability === 0)
      // Si il n'y a pas de requête ou mise à jour en cours
      && !(this.requestInProgress || this.updating);
  }

  // Increase or Decrease buttons
  public onQuantityIncreaseOrDecrease(value: number): void {
    /** CLA le 06/02/2019 on modifie la gestion de cette partie pour le décolisage.
     * En effet, le client peut être perturbé si il augmente la quantité et
     * qu'il se retrouve avec une quantité en décolisage alors qu'il s'attendait à
     * avoir la quantité suivante. Donc on souhaite matérialiser le choix direct
     * d'une valeur en décolisage.
     *
     * CLA le 15/02/2019 TODO : dans le "LAB", c'est l'inverse, il faut pouvoir
     * saisir des unités jusqu'à ce qu'on soit dans un multiple de colisage.
     * A partir du premier multiple de colisage, on ne laisse la possibilité de
     * ne faire que des multiples de colisage. Il faut définir un paramétre
     * spécifique pour identifier cette situation (paramétrage au niveau de la
     * société).
     *
     * BOE le 21/03/2019
     * J'ai ajouté le test sur le booleen isDecolise
    */
    var labTest = true;

    this.outer = parseInt(this.outer.toString(), 10);

    if (this.unpackedQuantity) {
      this.unpackedQuantity = parseInt(this.unpackedQuantity.toString(), 10);
    }

    // On effectue le calcul classique pour passer aux prochaines valeurs
    if (this.isDecolise) {
      this.productQuantity = this.productQuantity + value * this.unpackedQuantity;

      //ajout BOE
      if (this.productQuantity == this.outer)
        this.isDecolise = false;

    } else {
      this.productQuantity = this.productQuantity + value * this.outer;
      // La quantité est la quantité de décolisage
      if (this.productQuantity == this.unpackedQuantity) {
        // Si on augmente
        if (value > 0) {
          // On ne tient pas compte de la valeur de décolisage
          this.productQuantity = 0;
        }
      }
    }

    // Pas de quantité négative (arrive quand la valeur de décolisage est saisie)
    if (this.productQuantity < 0) {
      this.productQuantity = 0;

     

      if (this.unpackedQuantity) {
        this.unpackedQuantity = parseInt(this.unpackedQuantity.toString(), 10);
      }
      // Compute possibles values
      const possibleValues = [this.outer * value];

      if (this.unpackedQuantity && this.unpackedQuantity > 0) {
        possibleValues.push(this.unpackedQuantity * value);
      }

      // Sort for nearest first
      possibleValues.sort((a, b) => {
        if (a * value < b * value) {
          return -1;
        } else {
          return 1;
        }
      });
    }
     /**
       * Franco Unique
       */

      this.francoState.refreshFranco(this.contextService.fullContext.order, this.contextService.fullContext.customer.code, this.contextService.fullContext.company.code);
  }

  public validateInput(): void {
    if (isNaN(this.productQuantity) || this.productQuantity < 0) {
      this.productQuantity = 0;
    }

    // Validation pour colis et décolisage
    if (
      this.outer &&
      (this.productQuantity % this.outer !== 0) &&
      (this.productQuantity >= this.outer || this.productQuantity % this.unpackedQuantity !== 0)
    ) {
      this.productQuantity = Math.round(this.productQuantity / this.outer) * this.outer;
      this.toastrService.warning(
        this.translate.instant('order.outerOrUnpackedQuantityError', {
          outer: this.outer,
          unpacked: this.unpackedQuantity
        })
      );
    }

    this.requestInProgress = true;
    this.userActionPending = false;


    // Appel au service pour mise à jour de la quantité
    this.orderService
      .postProductQuantity(
        this.contextService.context.companyCode,
        this.contextService.context.customerCode,
        this.contextService.context.orderCode,
        this.productCode,
        this.productQuantity,
        this.isDateCourte
      ).pipe(finalize(() => {
        this.productQuantity = this.oldQuantity;
        this.checkAddRemoveProduct();
        this.requestInProgress = false;
        this.cdr.markForCheck();
      }))
      .subscribe(
        updatedOrderFromServer => {
          // 🔹 On utilise l'ordre complet renvoyé par le serveur
          const newFullContext = {
            ...this.contextService.fullContext,
            order: updatedOrderFromServer
          };

          this.contextService.fullContext = newFullContext;
          this.contextService.fullContext$.next(newFullContext);

          // 🔹 On met à jour la quantité locale pour l'input
          const updatedLine = updatedOrderFromServer.lines.find(
            l => l.productCode === this.productCode && (!!l.isDateCourte) === (!!this.isDateCourte)
          );
          if (updatedLine) {


            // JDU: Tricky pas très sexy: Ici on gère l'affichage du message QUE lorsqu'on est en date courte, pour des
            // produits NON date courtes, c'est le server-error-interceptor qui gère l'affichage des messages d'erreur globaux.
            if (this.productQuantity > updatedLine.quantity && this.isDateCourte) {
              this.toastrService.warning(
                this.translate.instant('order.productQuantityIncreasedBySystem', {
                  productCode: this.productCode,
                }));
            }
            this.productQuantity = updatedLine.quantity;
            this.oldQuantity = updatedLine.quantity;
          } else {
            this.productQuantity = 0;
            this.oldQuantity = 0;
          }


            this.francoState.refreshFranco(this.contextService.fullContext.order, this.contextService.fullContext.customer.code, this.contextService.fullContext.company.code);

        }, error => {
          // JDU: Même remarque que plus haut, on gère l'affichage du message QUE pour les produits en date courte, en cas d'erreur, on
          // triche en disant en gros "Ce n'est pas une erreur, on a juste ajusté la quantité". Il faut vraiment qu'on revoit cette gestion des erreurs côté backend"
          if (this.isDateCourte) {
            this.toastrService.warning(
              this.translate.instant('order.productQuantityIncreasedBySystem', {
                productCode: this.productCode,
              }));
          }
        }
      );
  }

  public setUserActionPending(): void {
    this.userActionPending = true;
  }

  public onBlur(): void {
    if (this.availability?.availability === 0) {
      this.productQuantity = this.oldQuantity;
    }
  }

  // If the enter key is pressed, blur the focused input
  public onKeyUp(event): void {
    if (event.key === 'Enter') {
      event.target.blur();
    }
  }
}
